import type { EnvironmentSummary, HealthState, Seat } from '@central/api';
import { formatAgo, formatPercent, healthLabels, HealthChip, StatusChip } from '@central/ui';
import { Group, Select, Table } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { kindLabel, newestFirst, topologyLabel } from '../lib/environment';
import { enrollmentLabel, mono, muted } from '../lib/labels';
import { fullestSeat } from '../lib/seats';
import { DataTable } from './parts';

/**
 * Environments as a table. The overview variant (AdminOverview.dc.html) leads
 * with the customer and shows license use; the full variant (the Environments
 * screen) leads with the environment and shows kind, topology and enrollment.
 */
export function EnvironmentsTable({
  items,
  label,
  variant,
}: {
  items: EnvironmentSummary[];
  label: string;
  variant: 'overview' | 'full';
}) {
  const name = (e: EnvironmentSummary) => {
    const link = (
      <Link to="/environments/$environmentId" params={{ environmentId: e.id }}>
        {e.name}
      </Link>
    );
    if (!e.archivedAt) return link;
    return (
      <Group gap={8} wrap="nowrap">
        {link}
        <StatusChip tone="unk">Archived</StatusChip>
      </Group>
    );
  };
  const customers = (e: EnvironmentSummary) => e.customers.map((c) => c.name).join(', ') || '–';
  if (variant === 'overview')
    return (
      <DataTable
        label={label}
        head={[
          'Customer',
          'Environment',
          'Version',
          'Health',
          'Uptime 30 d',
          'Last heartbeat',
          'License use',
        ]}
      >
        {items.map((e) => (
          <Table.Tr key={e.id}>
            <Table.Td style={{ fontWeight: 600 }}>{customers(e)}</Table.Td>
            <Table.Td>{name(e)}</Table.Td>
            <Table.Td style={mono}>{e.version ?? '–'}</Table.Td>
            <Table.Td>
              <HealthChip state={e.health} />
            </Table.Td>
            <Table.Td style={mono}>{formatPercent(e.uptime30d)}</Table.Td>
            <Table.Td style={muted}>{formatAgo(e.lastHeartbeatAt)}</Table.Td>
            <Table.Td>
              <LicenseUse seats={e.seats} />
            </Table.Td>
          </Table.Tr>
        ))}
      </DataTable>
    );
  return (
    <DataTable
      label={label}
      minWidth={960}
      head={[
        'Environment',
        'Customers',
        'Kind',
        'Topology',
        'Version',
        'Health',
        'Enrollment',
        'Uptime 30 d',
        'Last heartbeat',
      ]}
    >
      {items.map((e) => (
        <Table.Tr key={e.id}>
          <Table.Td style={{ fontWeight: 600 }}>{name(e)}</Table.Td>
          <Table.Td>{customers(e)}</Table.Td>
          <Table.Td>{kindLabel[e.kind]}</Table.Td>
          <Table.Td>{e.topology ? topologyLabel[e.topology] : '–'}</Table.Td>
          <Table.Td style={mono}>{e.version ?? '–'}</Table.Td>
          <Table.Td>
            <HealthChip state={e.health} />
          </Table.Td>
          <Table.Td>{enrollmentLabel[e.enrollmentStatus]}</Table.Td>
          <Table.Td style={mono}>{formatPercent(e.uptime30d)}</Table.Td>
          <Table.Td style={muted}>{formatAgo(e.lastHeartbeatAt)}</Table.Td>
        </Table.Tr>
      ))}
    </DataTable>
  );
}

/** The seat nearest its limit, e.g. "482 / 500" titled with its feature. */
function LicenseUse({ seats }: { seats: Seat[] }) {
  const s = fullestSeat(seats);
  if (!s) return <span style={muted}>–</span>;
  return (
    <span style={mono} title={s.name}>
      {s.used} / {s.licensed}
    </span>
  );
}

const healthStates: HealthState[] = ['up', 'updating', 'degraded', 'down', 'unknown'];

/** A status filter's choice: one health state, or every environment needing attention. */
export type StatusChoice = HealthState | 'attention';

/** "Any status", one health state, and with `withAttention` "Needs attention". */
export function HealthFilter({
  value,
  onChange,
  withAttention = false,
}: {
  value: StatusChoice | undefined;
  onChange: (v: StatusChoice | undefined) => void;
  withAttention?: boolean;
}) {
  return (
    <Select
      aria-label="Status"
      size="sm"
      w={170}
      maw="100%"
      value={value ?? ''}
      onChange={(v) => onChange((v || undefined) as StatusChoice | undefined)}
      data={[
        { value: '', label: 'Any status' },
        ...(withAttention ? [{ value: 'attention', label: 'Needs attention' }] : []),
        ...healthStates.map((h) => ({ value: h, label: healthLabels[h] })),
      ]}
      allowDeselect={false}
    />
  );
}

/** "Any version" or one of the versions the fleet reports, newest first. */
export function VersionFilter({
  value,
  versions,
  onChange,
}: {
  value: string | undefined;
  versions: readonly string[];
  onChange: (v: string | undefined) => void;
}) {
  const options = newestFirst(value && !versions.includes(value) ? [...versions, value] : versions);
  return (
    <Select
      aria-label="Version"
      size="sm"
      w={180}
      maw="100%"
      value={value ?? ''}
      onChange={(v) => onChange(v || undefined)}
      data={[{ value: '', label: 'Any version' }, ...options.map((v) => ({ value: v, label: v }))]}
      allowDeselect={false}
    />
  );
}
